import { useState } from 'react';
import { OrganisationDto, RoleUserOrgAssociationDto } from '@/apiClient';
import { PrivateContextData } from '@/components/private/PrivateContext';
import { SELECTED_ORG_ID } from '@/configs/storageKeys';

export const usePrivateContextState = () => {
  // Initialised as `undefined` so consumers can distinguish "not fetched yet"
  // from "user has no memberships". `PostLoginFlow` later sets this to the
  // fetched array (or `[]` on fetch error). Anything reading
  // `userOrganisations?.length` keeps working unchanged.
  const [userOrganisations, setUserOrganisations] = useState<
    RoleUserOrgAssociationDto[] | undefined
  >(undefined);
  // Holds the resolved `OrganisationDto` (not the membership association), to
  // match `PrivateContextData.selectedOrganisation` in PrivateContext.
  const [selectedOrganisation, setSelectedOrganisation] = useState<
    OrganisationDto | undefined
  >(undefined);
  // Lazy initialiser + window guard — `PrivateStateProvider` wraps `Shell`
  // unconditionally so this hook also runs during SSR, where there is no
  // `window` / `sessionStorage`. The selected org id is hydrated from
  // sessionStorage on the client; on the server we start undefined.
  const [selectedOrganisationId, setSelectedOrganisationId] = useState<
    number | undefined
  >(() => {
    if (typeof window === 'undefined') return undefined;
    return Number(window.sessionStorage.getItem(SELECTED_ORG_ID)) || undefined;
  });
  const [showSplashScreen, setShowSplashScreen] = useState(true);
  // Holds the fields `Private` folds in once auth resolves
  // (userId / userGuid / userProfileApi / userProfile / loginFlowComplete /
  // etc.). The local state vars above + their setters + `setContextData`
  // itself are merged on top of this in `PrivateStateProvider` at render
  // time, so consumers of `usePrivate()` always see the union.
  const [contextData, setContextData] = useState<PrivateContextData>({});

  return {
    userOrganisations,
    setUserOrganisations,
    selectedOrganisation,
    setSelectedOrganisation,
    selectedOrganisationId,
    setSelectedOrganisationId,
    showSplashScreen,
    setShowSplashScreen,
    contextData,
    setContextData,
  };
};
